import * as ENGINE from '@gnsx/genesys.js';
import * as THREE from 'three';

/** Persisted inspector preview tonemapping keys (per-project localStorage). */
export const INSPECTOR_PREVIEW_TONE_MAPPING_CHOICES = [
  'scene',
  'linear',
  'reinhard',
  'cineon',
  'acesFilmic',
  'agx',
  'neutral',
  'none',
] as const;

export type InspectorPreviewToneMappingChoice =
  (typeof INSPECTOR_PREVIEW_TONE_MAPPING_CHOICES)[number];

const EXPLICIT_TONE_MAPPING: Record<
  Exclude<InspectorPreviewToneMappingChoice, 'scene'>,
  THREE.ToneMapping
> = {
  linear: THREE.LinearToneMapping,
  reinhard: THREE.ReinhardToneMapping,
  cineon: THREE.CineonToneMapping,
  acesFilmic: THREE.ACESFilmicToneMapping,
  agx: THREE.AgXToneMapping,
  neutral: THREE.NeutralToneMapping,
  none: THREE.NoToneMapping,
};

function getToneMappingDefaults(): ENGINE.ToneMappingEffectConfig {
  return ENGINE.DefaultPostprocessEffectConfigs[
    ENGINE.PostProcessPass.ToneMapping
  ] as ENGINE.ToneMappingEffectConfig;
}

/**
 * Resolves effective tone mapping + exposure for inspector thumbnails and preview.
 *
 * @param choice - Stored preference; `'scene'` uses the active world's post-process config when available.
 * @param world - Current editor world, or null.
 */
export function resolvePreviewToneMapping(
  choice: InspectorPreviewToneMappingChoice,
  world: ENGINE.World | null
): { mode: THREE.ToneMapping; exposure: number } {
  if (choice !== 'scene') {
    return {
      mode: EXPLICIT_TONE_MAPPING[choice],
      exposure: 1.0,
    };
  }

  const defaults = getToneMappingDefaults();
  const mgr = world?.postProcessManager;
  const cfg = mgr
    ? (mgr.getEffectConfig(ENGINE.PostProcessPass.ToneMapping) as
        | ENGINE.ToneMappingEffectConfig
        | null)
    : null;

  return {
    mode:
      cfg?.mode ??
      defaults.mode ??
      THREE.NoToneMapping,
    exposure: cfg?.exposure ?? defaults.exposure ?? 1.0,
  };
}

/** UI labels matching property editor naming; order for select items (None last in UI, not here). */
export const PREVIEW_TONE_MAPPING_OPTIONS: {
  value: InspectorPreviewToneMappingChoice;
  label: string;
}[] = [
  { value: 'scene', label: 'Scene' },
  { value: 'linear', label: 'Linear' },
  { value: 'reinhard', label: 'Reinhard' },
  { value: 'cineon', label: 'Cineon' },
  { value: 'acesFilmic', label: 'ACES Filmic' },
  { value: 'agx', label: 'AGX' },
  { value: 'neutral', label: 'Neutral' },
  { value: 'none', label: 'None' },
];
